<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useAppStore } from "@/store/modules/app";
import { useUserStore } from "@/store/modules/user";
import router from "@/router";
import { createIdleMonitor } from "@/utils/idleSession";
import { ConfigGlobal } from "@/components/ConfigGlobal";
import { useDesign } from "@/hooks/web/useDesign";
import { setCssVar } from "@/utils";

const { getPrefixCls } = useDesign();

const prefixCls = getPrefixCls("app");

const appStore = useAppStore();
const userStore = useUserStore();
let idleMonitor: ReturnType<typeof createIdleMonitor> | undefined;
let disposed = false;

watch(
  () => [userStore.getToken, router.currentRoute.value.path],
  () => idleMonitor?.sync(),
);

onMounted(async () => {
  await router.isReady();
  if (disposed) return;
  idleMonitor = createIdleMonitor({
    getToken: () => userStore.getToken,
    getPath: () => router.currentRoute.value.path,
    onTimeout: () => userStore.expireSession(true),
    onSessionEnded: () => userStore.expireSession(),
    onSessionChanged: () => window.location.reload(),
  });
  idleMonitor.sync(true);
});

onBeforeUnmount(() => {
  disposed = true;
  idleMonitor?.stop();
});

const currentSize = computed(() => appStore.getCurrentSize);

const greyMode = computed(() => appStore.getGreyMode);

appStore.initTheme();
// 强制设置Element Plus主题色
setCssVar("--el-color-primary", "#60f8b6");

// 检查并执行版本迁移（备用方案，主要迁移在 persist.beforeRestore 中处理）
onMounted(() => {
  // 迁移逻辑在 persist.beforeRestore 中处理，这里作为额外的安全检查
  // 确保即使 beforeRestore 没有执行，也能进行迁移
  appStore.migrateStoreVersion();

  // 添加调试日志
  console.warn("[Store Migration] App mounted, current state:", {
    storeVersion: appStore.storeVersion,
    tagsView: appStore.tagsView,
  });
});
</script>

<template>
  <ConfigGlobal :size="currentSize">
    <RouterView :class="greyMode ? `${prefixCls}-grey-mode` : ''" />
  </ConfigGlobal>
</template>

<style lang="less">
@prefix-cls: ~"@{namespace}-app";

.size {
  width: 100%;
  height: 100%;
}

html,
body {
  padding: 0 !important;
  margin: 0;
  overflow: hidden;
  .size;

  #app {
    .size;
  }
}

.@{prefix-cls}-grey-mode {
  filter: grayscale(100%);
}
</style>
